export interface DishItem {
  name: string
  got: number
  need: number
  icon?: string
  people?: string[]
}

// Chunky filled segments — used on detail pages
export function DishSegments({ items }: { items: DishItem[] }) {
  return (
    <div className="flex flex-col gap-3">
      {items.map((d, i) => {
        const segs = Array.from({ length: d.need }, (_, k) => k < d.got)
        const full = d.got >= d.need
        return (
          <div key={i} className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-[10px] bg-s2 border border-line-soft flex items-center justify-center text-ink-2 text-[14px] shrink-0">
              {d.icon ?? '◔'}
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-baseline mb-1.5">
                <div className="text-[14px] font-medium">{d.name}</div>
                <div className="text-[11px] text-muted mono">
                  {d.got}/{d.need}{full && ' ✓'}
                </div>
              </div>
              <div className="flex gap-1">
                {segs.map((on, k) => (
                  <div
                    key={k}
                    className={`flex-1 h-2 rounded-[4px] ${on ? (full ? 'bg-good' : 'bg-primary') : 'bg-s2'}`}
                    style={!on ? { border: '1px dashed var(--line)' } : undefined}
                  />
                ))}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Mini bars — for cards and admin views
export function DishMiniBars({ items, compact }: { items: DishItem[]; compact?: boolean }) {
  return (
    <div className={`flex flex-col ${compact ? 'gap-1.5' : 'gap-2'}`}>
      {items.map((d, i) => {
        const pct = Math.min(1, d.got / d.need)
        return (
          <div key={i} className="flex items-center gap-2.5">
            <div className="w-[60px] text-[11px] text-ink-2 font-medium">{d.name}</div>
            <div className="flex-1 h-1.5 rounded-[3px] bg-s2 relative overflow-hidden border border-line-soft">
              <div
                className={`absolute inset-0 rounded-[3px] ${pct >= 1 ? 'bg-good' : 'bg-primary'}`}
                style={{ right: `${(1 - pct) * 100}%` }}
              />
            </div>
            <div className="text-[10px] text-muted w-[22px] text-right mono">
              {d.got}/{d.need}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Roster chips — names + open slots
export function DishRoster({ items }: { items: DishItem[] }) {
  return (
    <div className="flex flex-col gap-2.5">
      {items.map((d, i) => (
        <div key={i} className="flex items-center gap-2.5 px-3 py-2.5 bg-s2 rounded-xl border border-line-soft">
          <div className="text-[13px] font-medium w-[72px] text-ink-2">{d.name}</div>
          <div className="flex-1 flex flex-wrap gap-1">
            {(d.people ?? []).map((p, k) => (
              <span key={k} className="chip chip--solid h-[22px] text-[11px] px-2">{p}</span>
            ))}
            {Array.from({ length: Math.max(0, d.need - (d.people?.length ?? 0)) }).map((_, k) => (
              <span
                key={'e' + k}
                className="h-[22px] px-2 inline-flex items-center rounded-full border text-muted text-[11px] mono tracking-[0.06em]"
                style={{ borderStyle: 'dashed' }}
              >
                open
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
